import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Calendar, CalendarDays, Star, Percent, DollarSign, ListTodo, LogOut, Trash2, Check, Plus, Calculator, Gift, CreditCard, MapPin, Image as ImageIcon, Users, Ban } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, LOGO } from "../lib/api";
import { PACKAGES, ADDONS } from "../data/content";
import AdminCalendar from "../components/AdminCalendar";
import MoneyTab from "./admin/MoneyTab";
import QuoteTab from "./admin/QuoteTab";

const TABS = [
  { k: "stats", label: "Overview", icon: LayoutDashboard },
  { k: "bookings", label: "Booked Clients", icon: Calendar },
  { k: "calendar", label: "Calendar", icon: CalendarDays },
  { k: "schedule", label: "Block Times", icon: Ban },
  { k: "money", label: "Money", icon: DollarSign },
  { k: "quote", label: "In-Person Quote", icon: Calculator },
  { k: "coupons", label: "Coupons", icon: Gift },
  { k: "giftcards", label: "Gift Cards", icon: CreditCard },
  { k: "gallery", label: "Before/After", icon: ImageIcon },
  { k: "leads", label: "Client Leads", icon: Users },
  { k: "discounts", label: "Discounts", icon: Percent },
  { k: "reviews", label: "Reviews", icon: Star },
  { k: "tasks", label: "Tasks", icon: ListTodo },
];

export default function AdminConsole() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ name: "", code: "", percent: 0 });
  const [newTask, setNewTask] = useState({ title: "", cadence: "normal" });
  const [adj, setAdj] = useState({ revenue: "", tips: "", upcoming: "" });
  const [coupons, setCoupons] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [newGallery, setNewGallery] = useState({ before: "", after: "", caption: "" });
  const [giftcards, setGiftcards] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ label: "", amount: "", category: "chemical" });
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: "", slot: "*", repeat: "none" });

  // Quote calculator state
  const [quoteItems, setQuoteItems] = useState([]);
  const [qPkg, setQPkg] = useState(PACKAGES[0].key);
  const [qSize, setQSize] = useState("sedan");
  const [qAddonKey, setQAddonKey] = useState(ADDONS[0].key);
  const [qAddonTier, setQAddonTier] = useState(0);
  const [qAddonName, setQAddonName] = useState("");
  const [qAddonPrice, setQAddonPrice] = useState("");
  const [chargePct, setChargePct] = useState(70);

  const loadAll = async () => {
    const [s, b, d, r, t, c, g, gc, ex, bl] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/bookings"), api.get("/admin/discounts"), api.get("/admin/reviews"), api.get("/admin/tasks"), api.get("/admin/coupons"), api.get("/gallery"), api.get("/admin/giftcards"), api.get("/admin/expenses"), api.get("/admin/blocked-slots"),
    ]);
    setStats(s.data); setBookings(b.data.bookings); setDiscounts(d.data.discounts); setActiveCount(d.data.active_count); setReviews(r.data.reviews); setTasks(t.data.tasks); setCoupons(c.data.coupons); setGallery(g.data.gallery); setGiftcards(gc.data.giftcards); setExpenses(ex.data.expenses); setBlockedSlots(bl.data.blocked);
  };
  useEffect(() => { loadAll(); }, []);

  const completeBooking = async (id) => {
    const tip = parseFloat(prompt("Detail complete! How much did they tip? ($)", "0") || "0");
    await api.patch(`/admin/bookings/${id}`, { status: "completed", tip: isNaN(tip) ? 0 : tip });
    toast.success("Marked complete"); loadAll();
  };
  const cancelBooking = async (id) => { await api.patch(`/admin/bookings/${id}`, { status: "cancelled" }); toast.success("Cancelled"); loadAll(); };
  const deleteBooking = async (id) => { await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); loadAll(); };

  const addDiscount = async () => {
    if (!newDiscount.name) return toast.error("Name required");
    if (activeCount >= 5) toast.warning("You have 5+ active discounts. Try to stay under 5.");
    await api.post("/admin/discounts", { ...newDiscount, percent: parseFloat(newDiscount.percent) || 0, active: true, category: "active" });
    setNewDiscount({ name: "", code: "", percent: 0 }); loadAll();
  };
  const toggleDiscount = async (d) => { await api.patch(`/admin/discounts/${d.id}`, { active: !d.active }); loadAll(); };
  const delDiscount = async (id) => { await api.delete(`/admin/discounts/${id}`); loadAll(); };

  const approveReview = async (id, approved) => { await api.patch(`/admin/reviews/${id}`, { approved }); loadAll(); };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); loadAll(); };

  const addTask = async () => { if (!newTask.title) return; await api.post("/admin/tasks", newTask); setNewTask({ title: "", cadence: "normal" }); loadAll(); };
  const toggleTask = async (t) => { await api.patch(`/admin/tasks/${t.id}`, { done: !t.done }); loadAll(); };
  const delTask = async (id) => { await api.delete(`/admin/tasks/${id}`); loadAll(); };

  const addAdjustment = async () => {
    const payload = { revenue: parseFloat(adj.revenue) || 0, tips: parseFloat(adj.tips) || 0, upcoming: parseInt(adj.upcoming) || 0 };
    if (!payload.revenue && !payload.tips && !payload.upcoming) return toast.error("Enter an amount to add.");
    await api.post("/admin/adjustments", payload);
    setAdj({ revenue: "", tips: "", upcoming: "" }); toast.success("Totals updated"); loadAll();
  };

  const addPkgToQuote = () => { const p = PACKAGES.find((x) => x.key === qPkg); setQuoteItems([...quoteItems, { id: crypto.randomUUID(), label: `${p.name} (${qSize === "sedan" ? "Sedan" : "SUV/Truck"})`, price: qSize === "sedan" ? p.sedan : p.suv }]); };
  const addAddonToQuote = () => { if (!qAddonName || !qAddonPrice) return toast.error("Add-on name & price required"); setQuoteItems([...quoteItems, { id: crypto.randomUUID(), label: qAddonName, price: parseFloat(qAddonPrice) || 0 }]); setQAddonName(""); setQAddonPrice(""); };
  const removeQuoteItem = (i) => setQuoteItems(quoteItems.filter((_, idx) => idx !== i));
  const quoteTotal = quoteItems.reduce((a, b) => a + b.price, 0);
  const qAddonObj = ADDONS.find((a) => a.key === qAddonKey) || ADDONS[0];
  const addFirmAddon = () => { const a = qAddonObj; const t = a.tiers[Math.min(qAddonTier, a.tiers.length - 1)]; const size = qSize === "sedan" ? "sedan" : "suv"; setQuoteItems([...quoteItems, { id: crypto.randomUUID(), label: `${a.name}${a.tiers.length > 1 ? " — " + t.label : ""} (${size === "sedan" ? "Sedan" : "SUV/Truck"})`, price: t[size] }]); };
  const chargeNow = Math.round(quoteTotal * (Number(chargePct) || 0)) / 100;

  const readImg = (file) => new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(file); });
  const onGalleryFile = async (key, e) => { const f = e.target.files[0]; if (!f) return; const b64 = await readImg(f); setNewGallery((g) => ({ ...g, [key]: b64 })); e.target.value = ""; };
  const addGallery = async () => { if (!newGallery.before || !newGallery.after) return toast.error("Add both before & after photos."); await api.post("/admin/gallery", newGallery); setNewGallery({ before: "", after: "", caption: "" }); toast.success("Added to gallery"); loadAll(); };
  const delGallery = async (id) => { await api.delete(`/admin/gallery/${id}`); loadAll(); };
  const delCoupon = async (id) => { await api.delete(`/admin/coupons/${id}`); loadAll(); };
  const delGiftcard = async (id) => { await api.delete(`/admin/giftcards/${id}`); loadAll(); };
  const addExpense = async () => { if (!newExpense.label || !newExpense.amount) return toast.error("Label & amount required"); await api.post("/admin/expenses", { ...newExpense, amount: parseFloat(newExpense.amount) || 0 }); setNewExpense({ label: "", amount: "", category: "chemical" }); toast.success("Expense logged"); loadAll(); };
  const delExpense = async (id) => { await api.delete(`/admin/expenses/${id}`); loadAll(); };
  const addBlock = async () => {
    if (newBlock.repeat === "none") {
      if (!newBlock.date) return toast.error("Pick a date");
      const dd = new Date(newBlock.date + "T00:00:00");
      if (dd.getDay() !== 0 && dd.getDay() !== 6) return toast.error("Only weekend dates can be blocked.");
      await api.post("/admin/blocked-slots", { date: newBlock.date, slot: newBlock.slot });
    } else {
      await api.post("/admin/blocked-slots", { recurring: true, weekday: newBlock.repeat === "sat" ? 5 : 6, slot: newBlock.slot });
    }
    setNewBlock({ date: "", slot: "*", repeat: "none" }); toast.success("Time blocked"); loadAll();
  };
  const delBlock = async (id) => { await api.delete(`/admin/blocked-slots/${id}`); loadAll(); };
  const markReviewed = async (id) => { await api.patch(`/admin/bookings/${id}`, { needs_travel_review: false }); toast.success("Marked reviewed"); loadAll(); };
  const sortedBookings = [...bookings].sort((a, b) => ((b.needs_travel_review && b.status !== "cancelled") ? 1 : 0) - ((a.needs_travel_review && a.status !== "cancelled") ? 1 : 0));
  const leads = bookings.filter((b) => { const q = leadSearch.toLowerCase(); return !q || (b.full_name || "").toLowerCase().includes(q) || (b.phone || "").includes(q) || (b.email || "").toLowerCase().includes(q); });

  const badge = (s) => ({ confirmed: "bg-green-500/20 text-green-400", pending_payment: "bg-amber-500/20 text-amber-400", completed: "bg-pink-500/20 text-pink-400", cancelled: "bg-red-500/20 text-red-400" }[s] || "bg-white/10");
  const input = "bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-3 py-2 outline-none";

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <aside className="w-16 md:w-60 border-r border-white/10 flex flex-col py-6 fixed h-full bg-[#0a0a0a] z-20">
        <img src={LOGO} alt="Pink Crew" className="h-9 w-auto rounded mx-auto md:mx-4 md:self-start mb-8" />
        <nav className="flex-1 space-y-1 px-2 overflow-y-auto">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${tab === t.k ? "bg-pink-600 text-white" : "text-zinc-400 hover:bg-white/5"}`} data-testid={`admin-tab-${t.k}`}>
              <t.icon size={18} /><span className="hidden md:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 mx-2 text-sm text-zinc-400 hover:text-white" data-testid="admin-logout"><LogOut size={18} /><span className="hidden md:inline">Logout</span></button>
      </aside>

      <main className="flex-1 ml-16 md:ml-60 p-6 md:p-10">
        {tab === "stats" && stats && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-6">Overview</h1>
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
              {[
                { l: "Revenue", v: `$${stats.revenue}` }, { l: "Upcoming jobs", v: stats.upcoming_count },
                { l: "Pending payment", v: stats.pending_count }, { l: "Completed", v: stats.completed_count },
              ].map((c) => (<div key={c.l} className="rounded-2xl border border-white/10 bg-[#121212] p-6"><p className="text-sm text-zinc-400">{c.l}</p><p className="text-3xl font-extrabold mt-2 text-pink-500">{c.v}</p></div>))}
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-6">Booked Clients</h1>
            {sortedBookings.some((b) => b.needs_travel_review && b.status !== "cancelled") && (
              <div className="mb-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center gap-2 text-amber-200 text-sm" data-testid="review-queue-banner"><MapPin size={16} /> Some bookings have unverified addresses — confirm travel before the appointment. They're pinned to the top.</div>
            )}
            <div className="space-y-3">
              {sortedBookings.length === 0 && <p className="text-zinc-500">No bookings yet.</p>}
              {sortedBookings.map((b) => {
                const needsReview = b.needs_travel_review && b.status !== "cancelled";
                return (
                <div key={b.id} className={`rounded-2xl border bg-[#121212] p-5 flex flex-wrap items-center justify-between gap-4 ${needsReview ? "border-amber-500/50" : "border-white/10"}`} data-testid={`booking-${b.id}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap"><p className="font-bold">{b.full_name}</p><span className={`text-xs px-2 py-0.5 rounded-full ${badge(b.status)}`}>{b.status.replace("_", " ")}</span>{needsReview && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex items-center gap-1" data-testid={`review-badge-${b.id}`}><MapPin size={11} /> Needs travel review</span>}</div>
                    <p className="text-sm text-zinc-400 mt-1">{b.date} · {b.time_slot} · {b.cars?.length} car(s) · {b.phone} {b.email && `· ${b.email}`}</p>
                    <p className="text-sm text-zinc-500">{b.address} · Total ${b.total} · Deposit ${b.deposit_amount} {b.gift_applied > 0 && `· Gift -$${b.gift_applied}`} {b.tip > 0 && `· Tip $${b.tip}`}</p>
                  </div>
                  <div className="flex gap-2">
                    {needsReview && <button onClick={() => markReviewed(b.id)} className="text-xs bg-amber-500/80 hover:bg-amber-500 text-black rounded-full px-3 py-2 font-semibold" data-testid={`mark-reviewed-${b.id}`}>Mark reviewed</button>}
                    {b.status !== "completed" && <button onClick={() => completeBooking(b.id)} className="text-xs bg-pink-600/80 hover:bg-pink-600 rounded-full px-3 py-2" data-testid={`complete-${b.id}`}>Complete</button>}
                    {b.status !== "cancelled" && <button onClick={() => cancelBooking(b.id)} className="text-xs bg-white/5 border border-white/15 rounded-full px-3 py-2">Cancel</button>}
                    <button onClick={() => deleteBooking(b.id)} className="text-xs text-red-400 hover:bg-red-500/10 rounded-full px-2 py-2"><Trash2 size={14} /></button>
                  </div>
                </div>
              );})}
            </div>
          </div>
        )}

        {tab === "calendar" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-6">Booking Calendar</h1>
            <AdminCalendar bookings={bookings} />
          </div>
        )}

        {tab === "money" && stats && (
          <MoneyTab stats={stats} adj={adj} setAdj={setAdj} addAdjustment={addAdjustment} input={input}
            newExpense={newExpense} setNewExpense={setNewExpense} addExpense={addExpense} expenses={expenses} delExpense={delExpense} />
        )}

        {tab === "quote" && (
          <QuoteTab input={input}
            qPkg={qPkg} setQPkg={setQPkg} qSize={qSize} setQSize={setQSize} addPkgToQuote={addPkgToQuote}
            qAddonKey={qAddonKey} setQAddonKey={setQAddonKey} qAddonTier={qAddonTier} setQAddonTier={setQAddonTier}
            qAddonObj={qAddonObj} addFirmAddon={addFirmAddon}
            qAddonName={qAddonName} setQAddonName={setQAddonName} qAddonPrice={qAddonPrice} setQAddonPrice={setQAddonPrice} addAddonToQuote={addAddonToQuote}
            quoteItems={quoteItems} removeQuoteItem={removeQuoteItem} quoteTotal={quoteTotal}
            chargePct={chargePct} setChargePct={setChargePct} chargeNow={chargeNow} />
        )}

        {tab === "discounts" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-2">Managing Discounts</h1>
            <p className={`text-sm mb-6 ${activeCount >= 5 ? "text-amber-400" : "text-zinc-500"}`}>{activeCount} active · Max 3 promos stacked per booking; try to stay under 5 active discounts.</p>
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-6 flex flex-wrap gap-3 items-end">
              <input value={newDiscount.name} onChange={(e) => setNewDiscount({ ...newDiscount, name: e.target.value })} placeholder="Discount name" className={`${input} flex-1 min-w-[140px]`} data-testid="disc-name" />
              <input value={newDiscount.code} onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value })} placeholder="Code" className={`${input} w-28`} data-testid="disc-code" />
              <input value={newDiscount.percent} onChange={(e) => setNewDiscount({ ...newDiscount, percent: e.target.value })} placeholder="%" type="number" className={`${input} w-20`} data-testid="disc-percent" />
              <button onClick={addDiscount} className="bg-pink-600 hover:bg-pink-500 rounded-full px-4 py-2 flex items-center gap-1 text-sm font-semibold" data-testid="disc-add"><Plus size={16} /> Add</button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {discounts.map((d) => (
                <div key={d.id} className="rounded-2xl border border-white/10 bg-[#121212] p-5 flex justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2"><p className="font-bold">{d.name}</p>{d.code && <span className="text-xs bg-white/10 rounded px-2 py-0.5 font-mono">{d.code}</span>}</div>
                    <p className="text-sm text-pink-500 font-semibold mt-1">{d.percent}% off</p>
                    {d.description && <p className="text-xs text-zinc-500 mt-1">{d.description}</p>}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <button onClick={() => toggleDiscount(d)} className={`text-xs rounded-full px-3 py-1 ${d.active ? "bg-green-500/20 text-green-400" : "bg-white/5 text-zinc-400"}`} data-testid={`disc-toggle-${d.id}`}>{d.active ? "Active" : "Inactive"}</button>
                    <button onClick={() => delDiscount(d.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-6">Reviews</h1>
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className="rounded-2xl border border-white/10 bg-[#121212] p-5 flex justify-between gap-4">
                  <div>
                    <p className="font-bold">{r.name} <span className="text-zinc-500 font-normal">· {r.rating}★ {r.location && `· ${r.location}`}</span></p>
                    <p className="text-sm text-zinc-400 mt-1">{r.text}</p>
                    {r.images && r.images.length > 0 && <div className="mt-2 flex gap-2">{r.images.map((img) => (<img key={img} src={img} alt="" className="w-14 h-14 object-cover rounded border border-white/10" />))}</div>}
                  </div>
                  <div className="flex gap-2 items-start">
                    <button onClick={() => approveReview(r.id, !r.approved)} className={`text-xs rounded-full px-3 py-1 ${r.approved ? "bg-green-500/20 text-green-400" : "bg-white/5"}`} data-testid={`review-approve-${r.id}`}>{r.approved ? "Published" : "Approve"}</button>
                    <button onClick={() => delReview(r.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "tasks" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-6">Task Management</h1>
            <div className="flex flex-wrap gap-3 mb-6">
              <input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} placeholder="New to-do…" className={`${input} flex-1 min-w-[200px]`} data-testid="task-title" />
              <select value={newTask.cadence} onChange={(e) => setNewTask({ ...newTask, cadence: e.target.value })} className={input} data-testid="task-cadence">
                <option value="normal">Normal</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
              <button onClick={addTask} className="bg-pink-600 hover:bg-pink-500 rounded-full px-4 py-2 flex items-center gap-1 text-sm font-semibold" data-testid="task-add"><Plus size={16} /> Add</button>
            </div>
            <div className="space-y-2">
              {tasks.map((t) => (
                <div key={t.id} className="rounded-xl border border-white/10 bg-[#121212] p-4 flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <button onClick={() => toggleTask(t)} className={`w-5 h-5 rounded border flex items-center justify-center ${t.done ? "bg-pink-600 border-pink-600" : "border-white/30"}`} data-testid={`task-toggle-${t.id}`}>{t.done && <Check size={12} />}</button>
                    <span className={t.done ? "line-through text-zinc-600" : ""}>{t.title}</span>
                    <span className="text-xs bg-white/10 rounded px-2 py-0.5">{t.cadence}</span>
                  </label>
                  <button onClick={() => delTask(t.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "coupons" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-2">Signature Upgrade Coupons</h1>
            <p className="text-sm text-zinc-400 mb-6">Every claimed "upgrade to Signature" offer appears here with its code and free-gift reward.</p>
            <div className="space-y-3">
              {coupons.length === 0 && <p className="text-zinc-500">No coupons claimed yet.</p>}
              {coupons.map((c) => (
                <div key={c.id} className="rounded-2xl border border-white/10 bg-[#121212] p-5 flex justify-between items-center" data-testid={`coupon-${c.id}`}>
                  <div><p className="font-mono font-bold text-pink-500 text-lg">{c.code}</p><p className="text-sm text-zinc-400">{c.reward} · {c.used ? "used" : "active"}</p></div>
                  <button onClick={() => delCoupon(c.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "giftcards" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-2">Gift Cards</h1>
            <p className="text-sm text-zinc-400 mb-6">Every gift card sold, with remaining balance and redemptions.</p>
            <div className="space-y-3">
              {giftcards.length === 0 && <p className="text-zinc-500">No gift cards sold yet.</p>}
              {giftcards.map((g) => (
                <div key={g.id} className="rounded-2xl border border-white/10 bg-[#121212] p-5 flex justify-between items-center gap-4" data-testid={`giftcard-${g.id}`}>
                  <div>
                    <p className="font-mono font-bold text-pink-500 text-lg">{g.code}</p>
                    <p className="text-sm text-zinc-400">${Number(g.amount).toFixed(2)} value · <span className="text-green-400">${Number(g.balance).toFixed(2)} left</span></p>
                    <p className="text-xs text-zinc-500 mt-1">{g.purchaser_email}{g.recipient_email && ` → ${g.recipient_email}`}{g.redemptions?.length > 0 && ` · ${g.redemptions.length} redemption(s)`}</p>
                  </div>
                  <button onClick={() => delGiftcard(g.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-6">Before / After Gallery</h1>
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-6 max-w-xl">
              <div className="grid grid-cols-2 gap-3">
                <label className="cursor-pointer border border-white/20 rounded-lg p-4 text-sm text-center hover:border-pink-500 transition-colors" data-testid="gallery-before-label">{newGallery.before ? "Before ✓" : "Upload BEFORE"}<input type="file" accept="image/*" className="hidden" onChange={(e) => onGalleryFile("before", e)} data-testid="gallery-before" /></label>
                <label className="cursor-pointer border border-white/20 rounded-lg p-4 text-sm text-center hover:border-pink-500 transition-colors" data-testid="gallery-after-label">{newGallery.after ? "After ✓" : "Upload AFTER"}<input type="file" accept="image/*" className="hidden" onChange={(e) => onGalleryFile("after", e)} data-testid="gallery-after" /></label>
              </div>
              <input value={newGallery.caption} onChange={(e) => setNewGallery({ ...newGallery, caption: e.target.value })} placeholder="Caption (optional)" className={`${input} w-full mt-3`} data-testid="gallery-caption" />
              <button onClick={addGallery} className="mt-3 bg-pink-600 hover:bg-pink-500 rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-1" data-testid="gallery-add"><Plus size={16} /> Add pair</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {gallery.map((g) => (
                <div key={g.id} className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden" data-testid={`admin-gallery-${g.id}`}>
                  <div className="grid grid-cols-2"><img src={g.before} className="h-32 w-full object-cover" alt="" /><img src={g.after} className="h-32 w-full object-cover" alt="" /></div>
                  <div className="flex justify-between items-center p-3"><span className="text-sm text-zinc-400">{g.caption || "—"}</span><button onClick={() => delGallery(g.id)} className="text-red-400"><Trash2 size={14} /></button></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "schedule" && (
          <div className="max-w-2xl">
            <h1 className="text-3xl font-extrabold tracking-tight mb-2">Block Weekend Times</h1>
            <p className="text-sm text-zinc-400 mb-6">Weekdays are closed to booking automatically. Each weekend slot now fits two services (e.g. one interior + one exterior) with only one full detail per slot. Block specific dates or set a recurring weekly block.</p>
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-6 flex flex-wrap gap-3 items-end">
              <div><label className="text-xs text-zinc-500">Repeat</label><select value={newBlock.repeat} onChange={(e) => setNewBlock({ ...newBlock, repeat: e.target.value })} className={`${input} block mt-1`} data-testid="block-repeat"><option value="none">One-time date</option><option value="sat">Every Saturday</option><option value="sun">Every Sunday</option></select></div>
              {newBlock.repeat === "none" && <div><label className="text-xs text-zinc-500">Weekend date</label><input type="date" value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} className={`${input} block mt-1`} data-testid="block-date" /></div>}
              <div><label className="text-xs text-zinc-500">Time slot</label><select value={newBlock.slot} onChange={(e) => setNewBlock({ ...newBlock, slot: e.target.value })} className={`${input} block mt-1`} data-testid="block-slot"><option value="*">Whole day</option><option value="9:00 AM">9:00 AM</option><option value="12:00 PM">12:00 PM</option><option value="3:00 PM">3:00 PM</option></select></div>
              <button onClick={addBlock} className="bg-pink-600 hover:bg-pink-500 rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1" data-testid="block-add"><Plus size={16} /> Block</button>
            </div>
            <div className="space-y-2">
              {blockedSlots.length === 0 && <p className="text-zinc-500 text-sm">No blocked times yet.</p>}
              {blockedSlots.map((b) => (
                <div key={b.id} className="rounded-xl border border-white/10 bg-[#121212] p-4 flex items-center justify-between" data-testid={`block-${b.id}`}>
                  <span className="text-sm">{b.recurring ? `Every ${b.weekday === 5 ? "Saturday" : "Sunday"}` : b.date} · {b.slot === "*" ? "Whole day blocked" : b.slot}</span>
                  <button onClick={() => delBlock(b.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-4">Client Leads</h1>
            <input value={leadSearch} onChange={(e) => setLeadSearch(e.target.value)} placeholder="Search name, phone or email…" className={`${input} w-full max-w-md mb-6`} data-testid="lead-search" />
            <div className="space-y-2">
              {leads.length === 0 && <p className="text-zinc-500">No leads yet.</p>}
              {leads.map((b) => (
                <div key={b.id} className="rounded-xl border border-white/10 bg-[#121212] p-4 flex justify-between gap-4" data-testid={`lead-${b.id}`}>
                  <div><p className="font-semibold">{b.full_name}</p><p className="text-sm text-zinc-400">{b.phone} {b.email && `· ${b.email}`}</p></div>
                  <div className="text-right text-sm text-zinc-500"><p>{b.date}</p><p>{b.status.replace("_", " ")}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
